import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg } from "../../lib/api";

const AVAIL = ["Available", "Pilot", "Planned", "Unavailable"];
const FIELDS = [["description", "Description"], ["problems", "Problems it solves"], ["prerequisites", "Required prerequisites"], ["limitations", "Integration limitations"]];

const ServiceCard = ({ s, onSaved }) => {
  const [f, setF] = useState(s);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/services/${s.id}`, { ...f, availability: f.availability || null });
      toast.success(`${data.name} saved`);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const canEnable = ["Available", "Pilot"].includes(f.availability);
  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm" data-testid={`service-card-${s.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input className="max-w-xs font-semibold" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid={`service-name-${s.key}`} />
        <div className="flex items-center gap-3">
          <Select value={f.availability || "unset"} onValueChange={(v) => { set("availability", v === "unset" ? null : v); if (!["Available", "Pilot"].includes(v)) set("sales_enabled", false); }}>
            <SelectTrigger className="w-40" data-testid={`service-availability-${s.key}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="unset">Not confirmed</SelectItem>{AVAIL.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs font-semibold"><Switch checked={!!f.sales_enabled} disabled={!canEnable} onCheckedChange={(v) => set("sales_enabled", v)} data-testid={`service-enabled-${s.key}`} />Use in recommendations</label>
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {FIELDS.map(([k, l]) => <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Textarea rows={2} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} /></div>)}
        <div className="space-y-1"><Label className="text-xs">Pricing (admin entered)</Label><Input value={f.pricing || ""} onChange={(e) => set("pricing", e.target.value)} placeholder="e.g. [enter pricing]" data-testid={`service-pricing-${s.key}`} /></div>
      </div>
      <Button size="sm" className="mt-3 bg-slate-900" onClick={save} data-testid={`service-save-${s.key}`}>Save</Button>
    </div>
  );
};

export default function AdminServices() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const enabled = items.filter((s) => s.sales_enabled).length;
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Admin</div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">Service catalog</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">Research only recommends services that are Available or Pilot <b>and</b> switched on for recommendations. {enabled} of {items.length} currently enabled. Pricing is never invented by the AI.</p>
      </div>
      <div className="space-y-4">{items.map((s) => <ServiceCard key={s.id} s={s} onSaved={(d) => setItems((xs) => xs.map((x) => (x.id === d.id ? d : x)))} />)}</div>
    </div>
  );
}
